import { utils } from "@bkry/bowline-connectors";
import { apiClient, applyHeaders, buildHeaders } from "@bkry/bowline-utils";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";
import { getCachedPayload } from "../cachedPayload";
import { storeIncluded } from "../dictionarySagas";
import { handleUnauthorized } from "../errorsSagas";
import { actions as signRequestActions } from "../signRequests/index";
import { actions as traitActions } from "../traits";
import { actions } from "./index";

/** List Saga
 *  @description: connects to the getEvent operation
 */
export function* list(action) {
  const locale = yield select((state) => state.i18nState.lang);
  let headers = yield buildHeaders(locale);
  let { projectId, pageNum, itemNum, sorting, filters, loadUserAbilities } =
    action.payload;

  const apiRequest = function* () {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Events.listEventsForProject,
      {
        project_id: projectId,
        page: pageNum,
        items: itemNum,
        sorting: JSON.stringify(sorting),
        filters: JSON.stringify(filters),
        load_user_abilities: loadUserAbilities,
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    return payload;
  };

  const successActions = function* (payload) {
    if (payload.obj.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.listSuccess(payload));
  };

  const failActions = function* (e) {
    yield put(actions.listFail({ message: e.message, ...e }));
    if (e.status === 403) {
      yield* handleUnauthorized({ payload: e });
    }
  };

  yield* getCachedPayload(
    "events",
    "list",
    apiRequest,
    successActions,
    failActions
  );
}

export function* create(action) {
  let headers = yield buildHeaders();
  let { project_id, ...requestBody } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.Events.createEventForProject,
      { project_id: project_id },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.createSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.createFail({ message: e.message, ...e }));
  }
}
/** Show Saga
 *  @description: connects to the showEvent operation
 *  @param {number} action.payload the Event id
 */
export function* show(action) {
  const locale = yield select((state) => state.i18nState.lang);
  let headers = yield buildHeaders(locale);
  const { projectId, id, loadUserAbilities } = action.payload;

  const apiRequest = function* () {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Events.showEventForProject,
      {
        project_id: projectId,
        id: id,
        load_user_abilities: loadUserAbilities,
      },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );
    return payload;
  };

  const successActions = function* (payload) {
    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.showSuccess(payload));
  };

  const failActions = function* (e) {
    yield put(actions.showFail({ message: e.message, ...e }));
    if (e.status === 403) {
      yield* handleUnauthorized({ payload: e });
    }
  };

  yield* getCachedPayload(
    "events",
    "show",
    apiRequest,
    successActions,
    failActions
  );
}

export function* update(action) {
  const { project_id, id, ...requestBody } = action.payload;
  let headers = yield buildHeaders();
  let includedTraitsDictionary = {};
  let remainingIncluded = [];
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.Events.updateEventForProject,
      { project_id: project_id, id: id },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    if (payload.obj?.included)
      payload.obj.included.forEach((entry) => {
        if (entry.type === "trait") {
          includedTraitsDictionary[entry.id] = entry;
        } else {
          remainingIncluded.push(entry);
        }
      });
    yield* storeIncluded({ payload: remainingIncluded });
    yield put(traitActions.mergeDictionary(includedTraitsDictionary));

    yield put(actions.updateSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.updateFail({ message: e.message, ...e }));
  }
}

export function* prepareUnlock(action) {
  const { isEth, isSol } = action.payload;
  if (isEth) {
    yield* prepareEthUnlock(action);
  } else if (isSol) {
    yield* prepareSolUnlock(action);
  }
}

export function* prepareEthUnlock(action) {
  let {
    account,
    connector,
    provider,
    token_identifier,
    unlock_boundary_id,
    project_title,
    contract_address,
    title,
    callbackSuccess,
    tokens,
    chainId,
  } = action.payload;
  try {
    //  Maybe lets add an issued at to the message and use this date also for unlocked_at in the api
    const signer = provider.getSigner();
    const { origin } = window.location;
    const message = `I want to sign up for the "${title}" on ${project_title}
with my NFTs:
${tokens
  .map((token) => `#${token.identifier} (Contract: ${token.contract_address})`)
  .join("\n")}

This message was signed with my Ethereum account:
${account}

URI: ${origin}
Version: 1
Chain ID: ${chainId}
`;
    const name = utils.getName(connector);
    if (name !== "BowlineWallet") {
      const signature = yield signer.signMessage(message);

      yield put(actions.prepareUnlockSuccess({}));
      callbackSuccess({
        signature,
        token_identifier,
        contract_address,
        unlock_boundary_id,
        tokens,
      });
    } else {
      let signaturePreview = "Event Unlocking";
      let messagePreview = {
        headline: `I want to sign up for the "${title}" on ${project_title} with my NFT #${token_identifier} (Contract: ${contract_address}).`,
        body: `This message was signed with my Ethereum account: ${account}`,
        terms: [`URI: ${origin}`, `Version: 1`, `Chain ID: ${chainId}`],
      };
      yield put(
        signRequestActions.addToDictionary({
          id: uuidv4(),
          message,
          signaturePreview,
          messagePreview,
          signer,
          callbackSuccess: (sig) =>
            callbackSuccess({
              token_identifier,
              contract_address,
              unlock_boundary_id,
              signature: sig,
              tokens,
            }),
          successAction: actions.prepareUnlockSuccess,
          failureAction: actions.prepareUnlockFail,
        })
      );
    }
  } catch (e) {
    yield put(actions.prepareUnlockFail({ message: e.message, ...e }));
  }
}

export function* prepareSolUnlock(action) {
  // TODO: We only implement solana unlocking for fungible tokens for now, handle the NFTs later
  let {
    account,
    project_title,
    title,
    callbackSuccess,
    contract_address,
    chainId,
    tokens,
    token_identifier,
    unlock_boundary_id,
  } = action.payload;
  try {
    //  Maybe lets add an issued at to the message and use this date also for unlocked_at in the api
    const { origin } = window.location;
    const message = `I want to unlock "${title}" on ${project_title}
with my proof of ownership of tokens from the contract ${contract_address}

This message was signed with my Solana account:
${account}

URI: ${origin}
Version: 1
`;

    const messageBytes = new TextEncoder().encode(message);

    const { signature } = yield window.solana.signMessage(messageBytes, "utf8");

    const signatureBase64 = Buffer.from(signature).toString("base64");
    console.log("Message signed successfully:", {
      signature: signatureBase64,
      account: account,
      message,
    });
    yield put(actions.prepareUnlockSuccess({}));
    callbackSuccess({
      signature: signatureBase64,
      token_identifier,
      contract_address,
      unlock_boundary_id,
      tokens,
    });
  } catch (e) {
    yield put(actions.prepareUnlockFail({ message: e.message, ...e }));
  }
}

export function* unlock(action) {
  let headers = yield buildHeaders();
  let {
    project_id,
    id,
    token_identifier,
    contract_address,
    unlock_boundary_id,
    unlock_input_values,
    signature,
    callbackSuccess,
    tokens,
  } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.Events.unlockEventForProject,
      { project_id: project_id, id: id },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody: {
          signature,
          tokens_attributes: tokens,
          unlock_boundary_id: unlock_boundary_id,
          unlock: {
            signature,
            tokens_attributes: tokens,
            unlock_boundary_id: unlock_boundary_id,
            unlock_input_values: unlock_input_values,
          },
        },
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    // TODO: reevaluate if showSuccess is right to call here, but doesn't feel to wrong to store the data
    yield put(actions.showSuccess(payload));
    yield put(actions.unlockSuccess(payload.obj.data));
    if (callbackSuccess) callbackSuccess();
  } catch (e) {
    yield put(actions.unlockFail({ message: e.message, ...e }));
  }
}

/**
 * Saga Watchers
 * The exported list of sagas registered. When one of the action types is dispatched
 * the related worker saga is invoked.
 * Each saga is executed in a different thread
 */
function* eventsSaga() {
  yield takeEvery(actions.list, list);
  yield takeEvery(actions.show, show);
  yield takeEvery(actions.update, update);
  yield takeEvery(actions.create, create);
  yield takeEvery(actions.prepareUnlock, prepareUnlock);
  yield takeEvery(actions.unlock, unlock);
}
export default eventsSaga;
